import { useCallback } from 'react'
import { AnimatedAxis } from '@visx/react-spring'
import { Axis as NonAnimatedAxis, Orientation } from '@visx/axis'

import { colors, fonts } from '#base/js/variables.style'
import { axisService } from './axis.service'

const FONT_FAMILY = fonts.lato
const FONT_SIZE = '12px'
const AXIS_COLOR = colors.gray
const TICK_LENGTH = 2
const TICK_LABEL_HEIGHT = 16
const LABEL_OFFSET_HORIZONTAL = 30
const LABEL_OFFSET_VERTICAL = 48

const DEFAULT_LABEL_PROPS = {
  fill: AXIS_COLOR,
  fontFamily: FONT_FAMILY,
  fontSize: FONT_SIZE,
  textAnchor: 'middle',
  scaleToFit: 'shrink-only'
}
const DEFAULT_TICK_LABEL_PROPS = {
  fill: AXIS_COLOR,
  fontFamily: FONT_FAMILY,
  fontSize: FONT_SIZE,
  lineHeight: '1em',
  scaleToFit: 'shrink-only'
}
const TICK_LABEL_PROPS = {
  [Orientation.bottom]: {
    dy: '0.2em',
    textAnchor: 'middle'
  },
  [Orientation.left]: {
    dx: '-0.25em',
    dy: '0.31em',
    textAnchor: 'end'
  },
  [Orientation.right]: {
    dx: '0.25em',
    dy: '0.31em',
    textAnchor: 'start'
  },
  [Orientation.top]: {
    dy: '-0.3em',
    textAnchor: 'middle'
  }
}

export function Axis ({
  width,
  height,
  orientation = Orientation.bottom,
  stroke = AXIS_COLOR,
  labelProps = {},
  tickLabelProps = {},
  tickStroke = AXIS_COLOR,
  tickLength = TICK_LENGTH,
  numTicks,
  tickFormat,
  ...props
}) {
  const defaultLabelOffset = axisService.isVertical(orientation) ? LABEL_OFFSET_VERTICAL : LABEL_OFFSET_HORIZONTAL
  let labelOffset = props.labelOffset ?? defaultLabelOffset

  labelProps = {
    ...DEFAULT_LABEL_PROPS,
    width: axisService.isVertical(orientation) ? height : width,
    ...labelProps
  }

  const getTickLabelProps = useCallback(() => {
    return { ...DEFAULT_TICK_LABEL_PROPS, ...TICK_LABEL_PROPS[orientation], ...tickLabelProps }
  }, [orientation, tickLabelProps])

  if (!numTicks) {
    const ticks = Array.isArray(props.tickValues) ? props.tickValues : props.scale?.ticks() ?? []
    const maxNumTicks = axisService.getMaxNumTicks({
      orientation,
      width,
      height,
      ticks,
      tickFormat,
      tickLabelProps: getTickLabelProps(),
      tickLabelHeight: TICK_LABEL_HEIGHT
    })
    if (maxNumTicks !== undefined) {
      numTicks = Math.min(ticks.length, maxNumTicks)
    }
  }

  const AxisComponent = props.animationTrajectory ? AnimatedAxis : NonAnimatedAxis

  return (
    <g data-qa={`axis-${orientation}`}>
      <AxisComponent
        orientation={orientation}
        stroke={stroke}
        labelOffset={labelOffset}
        labelProps={labelProps}
        tickStroke={tickStroke}
        tickLength={tickLength}
        tickLabelProps={getTickLabelProps}
        tickFormat={tickFormat}
        numTicks={numTicks}
        {...props}
      />
    </g>
  )
}
